// Generates math problems for a given operation + level.
// Returns: { question: string, answer: number, choices: number[] }

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function makeChoices(correct, level, operationId) {
  // Distractor spread depends on level + answer magnitude
  const spread = Math.max(2, Math.floor(Math.abs(correct) * 0.4) + (level === "dificil" ? 5 : 3));
  const set = new Set([correct]);
  let safety = 0;
  while (set.size < 4 && safety < 50) {
    safety++;
    const delta = randInt(-spread, spread);
    if (delta === 0) continue;
    let candidate = correct + delta;
    // Never produce negative options for primaria — the correct answer is always >= 0
    if (candidate < 0) candidate = Math.abs(candidate) + 1;
    if (candidate === correct) continue;
    set.add(candidate);
  }
  // Fallback: ensure 4 unique
  let i = 1;
  while (set.size < 4) {
    set.add(correct + i);
    i++;
  }
  return shuffle(Array.from(set));
}

function genSuma(level) {
  let a, b;
  if (level === "facil") {
    a = randInt(1, 20);
    b = randInt(1, 20);
  } else if (level === "medio") {
    a = randInt(10, 100);
    b = randInt(10, 100);
  } else {
    a = randInt(50, 500);
    b = randInt(50, 500);
  }
  const answer = a + b;
  return { question: `${a} + ${b}`, answer, choices: makeChoices(answer, level, "suma") };
}

function genResta(level) {
  let a, b;
  if (level === "facil") {
    a = randInt(5, 25);
    b = randInt(1, a);
  } else if (level === "medio") {
    a = randInt(30, 150);
    b = randInt(1, a);
  } else {
    a = randInt(100, 600);
    b = randInt(1, a);
  }
  const answer = a - b;
  return { question: `${a} − ${b}`, answer, choices: makeChoices(answer, level, "resta") };
}

function genMultiplicacion(level) {
  let a, b;
  if (level === "facil") {
    // Una cifra × una cifra (1-9)
    a = randInt(1, 9);
    b = randInt(1, 9);
  } else if (level === "medio") {
    // Dos cifras × dos cifras (10-99)
    a = randInt(10, 99);
    b = randInt(10, 99);
  } else {
    // Difícil: dos cifras × tres cifras
    a = randInt(10, 99);
    b = randInt(100, 999);
    // Aleatoriamente intercambiar el orden para variedad visual
    if (Math.random() < 0.5) [a, b] = [b, a];
  }
  const answer = a * b;
  return { question: `${a} × ${b}`, answer, choices: makeChoices(answer, level, "multiplicacion") };
}

function genDivision(level) {
  let b, answer;
  if (level === "facil") {
    b = randInt(2, 5);
    answer = randInt(1, 10);
  } else if (level === "medio") {
    b = randInt(2, 10);
    answer = randInt(2, 12);
  } else {
    b = randInt(3, 15);
    answer = randInt(5, 20);
  }
  const a = b * answer;
  return { question: `${a} ÷ ${b}`, answer, choices: makeChoices(answer, level, "division") };
}

function genPorcentajes(level) {
  // Calculate X% of N, ensuring integer answer.
  let percentChoices, baseChoices;
  if (level === "facil") {
    percentChoices = [10, 25, 50, 100];
    baseChoices = [10, 20, 40, 50, 60, 80, 100];
  } else if (level === "medio") {
    percentChoices = [10, 20, 25, 50, 75];
    baseChoices = [20, 40, 60, 80, 100, 120, 160, 200];
  } else {
    percentChoices = [15, 20, 25, 30, 40, 60, 75, 80];
    baseChoices = [50, 80, 100, 150, 200, 240, 300, 400, 500];
  }
  // Try until clean integer
  let percent, base, answer;
  for (let i = 0; i < 30; i++) {
    percent = percentChoices[randInt(0, percentChoices.length - 1)];
    base = baseChoices[randInt(0, baseChoices.length - 1)];
    answer = (percent * base) / 100;
    if (Number.isInteger(answer)) break;
  }
  return {
    question: `${percent}% de ${base}`,
    answer,
    choices: makeChoices(answer, level, "porcentajes"),
  };
}

function gcd(a, b) {
  a = Math.abs(a); b = Math.abs(b);
  while (b) { [a, b] = [b, a % b]; }
  return a || 1;
}

function simplify(n, d) {
  const g = gcd(n, d);
  return [n / g, d / g];
}

function formatFrac(n, d) {
  const [sn, sd] = simplify(n, d);
  if (sd === 1) return String(sn);
  return `${sn}/${sd}`;
}

function makeFractionChoices(correctStr) {
  const set = new Set([correctStr]);
  const isInt = !correctStr.includes("/");
  let safety = 0;
  while (set.size < 4 && safety < 80) {
    safety++;
    if (isInt) {
      const val = parseInt(correctStr, 10);
      const delta = randInt(-3, 3);
      const cand = Math.max(0, val + (delta === 0 ? 1 : delta));
      if (String(cand) !== correctStr) set.add(String(cand));
    } else {
      const [n, d] = correctStr.split("/").map(Number);
      const r = Math.random();
      let nn = n, dd = d;
      if (r < 0.33) {
        const dn = randInt(-2, 2);
        nn = Math.max(1, n + (dn === 0 ? 1 : dn));
      } else if (r < 0.66) {
        const dd2 = randInt(-2, 2);
        dd = Math.max(2, d + (dd2 === 0 ? 1 : dd2));
      } else {
        // Swap numerator and denominator (common kid mistake)
        if (n !== d) { nn = d; dd = n; }
      }
      if (nn === n && dd === d) continue;
      if (nn >= dd && nn > 1) nn = Math.max(1, dd - 1);
      if (dd < 2) continue;
      const cand = `${nn}/${dd}`;
      if (cand !== correctStr) set.add(cand);
    }
  }
  let i = 1;
  while (set.size < 4) {
    set.add(isInt ? String(parseInt(correctStr, 10) + i + 10) : `${i + 1}/${(parseInt(correctStr.split("/")[1] || "5", 10) + i)}`);
    i++;
  }
  const arr = Array.from(set);
  for (let i2 = arr.length - 1; i2 > 0; i2--) {
    const j = Math.floor(Math.random() * (i2 + 1));
    [arr[i2], arr[j]] = [arr[j], arr[i2]];
  }
  return arr;
}

// Generate operands a/d1 and b/d2 based on level/subOp configuration
function generateFractionOperands(level) {
  if (level === "facil") {
    const d = randInt(3, 8);
    const a = randInt(1, d - 1);
    const b = randInt(1, d - 1);
    return { a, d1: d, b, d2: d };
  }
  if (level === "medio") {
    const d1 = randInt(2, 5);
    const k = randInt(2, 3);
    const d2 = d1 * k;
    const a = randInt(1, d1 - 1 < 1 ? 1 : d1 - 1);
    const b = randInt(1, d2 - 1);
    return { a, d1, b, d2 };
  }
  // dificil
  let d1 = randInt(2, 6);
  let d2 = randInt(3, 7);
  if (d1 === d2) d2 = d2 === 7 ? 5 : d2 + 1;
  const a = randInt(1, d1 - 1 < 1 ? 1 : d1 - 1);
  const b = randInt(1, d2 - 1);
  return { a, d1, b, d2 };
}

function genFracSuma(level) {
  const { a, d1, b, d2 } = generateFractionOperands(level);
  const num = a * d2 + b * d1;
  const den = d1 * d2;
  const answer = formatFrac(num, den);
  return {
    question: `${a}/${d1} + ${b}/${d2}`,
    answer,
    choices: makeFractionChoices(answer),
    visuals: [
      { numerator: a, denominator: d1 },
      { numerator: b, denominator: d2 },
    ],
    operator: "+",
  };
}

function genFracResta(level) {
  // Ensure a/d1 > b/d2 for positive result
  let { a, d1, b, d2 } = generateFractionOperands(level);
  let num1 = a * d2;
  let num2 = b * d1;
  if (num1 <= num2) {
    // swap
    [a, d1, b, d2] = [b, d2, a, d1];
    num1 = a * d2;
    num2 = b * d1;
  }
  const den = d1 * d2;
  const answer = formatFrac(num1 - num2, den);
  return {
    question: `${a}/${d1} − ${b}/${d2}`,
    answer,
    choices: makeFractionChoices(answer),
    visuals: [
      { numerator: a, denominator: d1 },
      { numerator: b, denominator: d2 },
    ],
    operator: "−",
  };
}

function genFracMulti(level) {
  let a, b, d1, d2;
  if (level === "facil") {
    // Unit fractions: 1/a × 1/b
    a = 1;
    d1 = randInt(2, 6);
    b = 1;
    d2 = randInt(2, 6);
  } else if (level === "medio") {
    d1 = randInt(2, 5);
    d2 = randInt(2, 5);
    a = randInt(1, d1 - 1 < 1 ? 1 : d1 - 1);
    b = randInt(1, d2 - 1 < 1 ? 1 : d2 - 1);
  } else {
    d1 = randInt(3, 7);
    d2 = randInt(3, 7);
    a = randInt(1, d1 - 1);
    b = randInt(1, d2 - 1);
  }
  const num = a * b;
  const den = d1 * d2;
  const answer = formatFrac(num, den);
  return {
    question: `${a}/${d1} × ${b}/${d2}`,
    answer,
    choices: makeFractionChoices(answer),
    visuals: [
      { numerator: a, denominator: d1 },
      { numerator: b, denominator: d2 },
    ],
    operator: "×",
  };
}

function genFracDiv(level) {
  let a, b, d1, d2;
  if (level === "facil") {
    a = 1; b = 1;
    d1 = randInt(2, 6);
    d2 = randInt(2, 6);
    if (d1 === d2) d2 = d2 === 6 ? 3 : d2 + 1;
  } else if (level === "medio") {
    d1 = randInt(2, 5);
    d2 = randInt(2, 5);
    a = randInt(1, d1 - 1 < 1 ? 1 : d1 - 1);
    b = randInt(1, d2 - 1 < 1 ? 1 : d2 - 1);
  } else {
    d1 = randInt(3, 7);
    d2 = randInt(3, 7);
    a = randInt(1, d1 - 1);
    b = randInt(1, d2 - 1);
  }
  // (a/d1) ÷ (b/d2) = (a*d2)/(d1*b)
  const num = a * d2;
  const den = d1 * b;
  const answer = formatFrac(num, den);
  return {
    question: `${a}/${d1} ÷ ${b}/${d2}`,
    answer,
    choices: makeFractionChoices(answer),
    visuals: [
      { numerator: a, denominator: d1 },
      { numerator: b, denominator: d2 },
    ],
    operator: "÷",
  };
}

function genFracSimplificacion(level) {
  // Build an unsimplified fraction (displayN/displayD) whose reduced form is answerN/answerD.
  // Levels control the size of the simplified target and the multiplication factor.
  let numMin, numMax, denMin, denMax, factorMin, factorMax, maxDisplayDen;
  if (level === "facil") {
    numMin = 1; numMax = 3;
    denMin = 2; denMax = 7;
    factorMin = 2; factorMax = 5;
    maxDisplayDen = 35;
  } else if (level === "medio") {
    numMin = 1; numMax = 5;
    denMin = 3; denMax = 10;
    factorMin = 3; factorMax = 6;
    maxDisplayDen = 60;
  } else {
    numMin = 2; numMax = 12;
    denMin = 4; denMax = 15;
    factorMin = 4; factorMax = 10;
    maxDisplayDen = 150;
  }

  let simN = 1, simD = 2, factor = 2, dispN = 2, dispD = 4;
  for (let attempt = 0; attempt < 60; attempt++) {
    const n = randInt(numMin, numMax);
    const d = randInt(denMin, denMax);
    if (n >= d) continue;              // keep proper (< 1)
    if (gcd(n, d) !== 1) continue;      // must be already simplified
    const f = randInt(factorMin, factorMax);
    const dn = n * f;
    const dd = d * f;
    if (dd > maxDisplayDen) continue;
    simN = n; simD = d; factor = f; dispN = dn; dispD = dd;
    break;
  }

  const answer = `${simN}/${simD}`;
  return {
    question: `${dispN}/${dispD}`,
    answer,
    choices: makeFractionChoices(answer),
    visuals: [{ numerator: dispN, denominator: dispD }],
    operator: "",
  };
}

const FRAC_SUB_GENERATORS = {
  suma: genFracSuma,
  resta: genFracResta,
  multiplicacion: genFracMulti,
  division: genFracDiv,
  simplificacion: genFracSimplificacion,
};

function genFracciones(level, subOp = "suma") {
  const fn = FRAC_SUB_GENERATORS[subOp] || genFracSuma;
  return fn(level);
}

// ---------- DECIMAL helpers ----------
function fmtDec(x, places) {
  const f = Math.pow(10, places);
  return (Math.round(x * f) / f).toFixed(places);
}

function makeDecimalChoices(correctNum, places, spreadInt = 3) {
  const correctStr = fmtDec(correctNum, places);
  const step = 1 / Math.pow(10, places);
  const set = new Set([correctStr]);
  let attempts = 0;
  while (set.size < 4 && attempts < 60) {
    attempts++;
    // Common kid errors: forget to carry, misplace decimal, off-by-1 digit
    const errKind = Math.floor(Math.random() * 3);
    let cand;
    if (errKind === 0) {
      // small numeric delta
      const delta = randInt(-spreadInt, spreadInt);
      if (delta === 0) continue;
      cand = correctNum + delta * step * randInt(1, 9);
    } else if (errKind === 1) {
      // off by 0.1 / 1.0
      const delta = randInt(-3, 3);
      if (delta === 0) continue;
      cand = correctNum + delta * 0.1;
    } else {
      // off by 1 in integer part
      const delta = randInt(-2, 2);
      if (delta === 0) continue;
      cand = correctNum + delta;
    }
    if (cand < 0) cand = Math.abs(cand);
    const candStr = fmtDec(cand, places);
    if (candStr !== correctStr) set.add(candStr);
  }
  let i = 1;
  while (set.size < 4) {
    set.add(fmtDec(correctNum + i + 1, places));
    i++;
  }
  const arr = Array.from(set);
  for (let i2 = arr.length - 1; i2 > 0; i2--) {
    const j = Math.floor(Math.random() * (i2 + 1));
    [arr[i2], arr[j]] = [arr[j], arr[i2]];
  }
  return arr;
}

function genDecSuma(level) {
  let a, b, places;
  if (level === "facil") {
    places = 1;
    a = randInt(1, 99) / 10;
    b = randInt(1, 99) / 10;
  } else if (level === "medio") {
    places = 1;
    a = randInt(10, 500) / 10;
    b = randInt(10, 500) / 10;
  } else {
    places = 2;
    a = randInt(50, 2000) / 100;
    b = randInt(50, 2000) / 100;
  }
  const answer = fmtDec(a + b, places);
  return {
    question: `${fmtDec(a, places)} + ${fmtDec(b, places)}`,
    answer,
    choices: makeDecimalChoices(a + b, places),
  };
}

function genDecResta(level) {
  let a, b, places;
  if (level === "facil") {
    places = 1;
    a = randInt(20, 99) / 10;
    b = randInt(1, Math.floor(a * 10)) / 10;
  } else if (level === "medio") {
    places = 1;
    a = randInt(50, 500) / 10;
    b = randInt(10, Math.floor(a * 10)) / 10;
  } else {
    places = 2;
    a = randInt(200, 2000) / 100;
    b = randInt(50, Math.floor(a * 100)) / 100;
  }
  const answer = fmtDec(a - b, places);
  return {
    question: `${fmtDec(a, places)} − ${fmtDec(b, places)}`,
    answer,
    choices: makeDecimalChoices(a - b, places),
  };
}

function genDecMulti(level) {
  // Multiplication of decimals: places of result = places(a) + places(b)
  let aStr, bStr, aNum, bNum, resultPlaces;
  if (level === "facil") {
    // decimal × integer (1..9)
    aNum = randInt(1, 99) / 10;
    bNum = randInt(2, 9);
    aStr = fmtDec(aNum, 1);
    bStr = String(bNum);
    resultPlaces = 1;
  } else if (level === "medio") {
    // 1-decimal × 1-decimal
    aNum = randInt(11, 99) / 10;
    bNum = randInt(11, 99) / 10;
    aStr = fmtDec(aNum, 1);
    bStr = fmtDec(bNum, 1);
    resultPlaces = 2;
  } else {
    // 2-decimal × 1-decimal
    aNum = randInt(101, 999) / 100;
    bNum = randInt(11, 49) / 10;
    aStr = fmtDec(aNum, 2);
    bStr = fmtDec(bNum, 1);
    resultPlaces = 3;
  }
  const product = aNum * bNum;
  const answer = fmtDec(product, resultPlaces);
  return {
    question: `${aStr} × ${bStr}`,
    answer,
    choices: makeDecimalChoices(product, resultPlaces),
  };
}

const DEC_SUB_GENERATORS = {
  suma: genDecSuma,
  resta: genDecResta,
  multiplicacion: genDecMulti,
};

function genDecimales(level, subOp = "suma") {
  const fn = DEC_SUB_GENERATORS[subOp] || genDecSuma;
  return fn(level);
}

// ---------- ALGEBRA helpers ----------
const SUPERSCRIPTS = { 0: "⁰", 1: "¹", 2: "²", 3: "³", 4: "⁴", 5: "⁵", 6: "⁶", 7: "⁷", 8: "⁸", 9: "⁹" };
function toSuper(n) {
  return String(n).split("").map((d) => SUPERSCRIPTS[d] || d).join("");
}

function fmtTermLeading(coef, exp) {
  // First term of a polynomial — keeps own sign
  if (coef === 0) return "";
  const sign = coef < 0 ? "−" : "";
  const abs = Math.abs(coef);
  if (exp === 0) return `${sign}${abs}`;
  const coefStr = abs === 1 ? "" : String(abs);
  const expStr = exp === 1 ? "" : toSuper(exp);
  return `${sign}${coefStr}x${expStr}`;
}

function fmtTermNext(coef, exp) {
  // Subsequent term — sign becomes operator with spaces
  if (coef === 0) return "";
  const op = coef < 0 ? " − " : " + ";
  const abs = Math.abs(coef);
  if (exp === 0) return `${op}${abs}`;
  const coefStr = abs === 1 ? "" : String(abs);
  const expStr = exp === 1 ? "" : toSuper(exp);
  return `${op}${coefStr}x${expStr}`;
}

function fmtPoly(coefs) {
  // coefs: { 0: const, 1: x, 2: x², ... }
  const exps = Object.keys(coefs).map(Number).filter((e) => coefs[e] !== 0).sort((a, b) => b - a);
  if (exps.length === 0) return "0";
  let result = fmtTermLeading(coefs[exps[0]], exps[0]);
  for (let i = 1; i < exps.length; i++) {
    result += fmtTermNext(coefs[exps[i]], exps[i]);
  }
  return result;
}

function fmtSinglePoly(coefs) {
  // For inline rendering of input operands (uses parentheses if more than 1 non-zero term)
  const exps = Object.keys(coefs).map(Number).filter((e) => coefs[e] !== 0).sort((a, b) => b - a);
  if (exps.length === 0) return "0";
  // single term: no parens needed
  if (exps.length === 1) return fmtTermLeading(coefs[exps[0]], exps[0]);
  let r = fmtTermLeading(coefs[exps[0]], exps[0]);
  for (let i = 1; i < exps.length; i++) r += fmtTermNext(coefs[exps[i]], exps[i]);
  return r;
}

function makeIntegerChoices(correct, spread = 4) {
  const set = new Set([String(correct)]);
  let attempts = 0;
  while (set.size < 4 && attempts < 50) {
    attempts++;
    const delta = randInt(-spread, spread);
    if (delta === 0) continue;
    const cand = correct + delta;
    if (String(cand) !== String(correct)) set.add(String(cand));
  }
  let i = 1;
  while (set.size < 4) { set.add(String(correct + i + 10)); i++; }
  const arr = Array.from(set);
  for (let i2 = arr.length - 1; i2 > 0; i2--) {
    const j = Math.floor(Math.random() * (i2 + 1));
    [arr[i2], arr[j]] = [arr[j], arr[i2]];
  }
  return arr;
}

function makePolyChoices(correctCoefs) {
  // Perturb 1-2 coefficients by ±1 or ±2 to create distractors
  const correctStr = fmtPoly(correctCoefs);
  const set = new Set([correctStr]);
  const exps = Object.keys(correctCoefs).map(Number);
  let attempts = 0;
  while (set.size < 4 && attempts < 60) {
    attempts++;
    const next = { ...correctCoefs };
    const exp = exps[randInt(0, exps.length - 1)];
    const delta = randInt(-2, 2);
    if (delta === 0) continue;
    next[exp] = (next[exp] || 0) + delta;
    const candStr = fmtPoly(next);
    if (candStr !== correctStr) set.add(candStr);
  }
  let i = 1;
  while (set.size < 4) {
    const fb = { ...correctCoefs };
    const e = exps[0];
    fb[e] = (fb[e] || 0) + (i + 5);
    set.add(fmtPoly(fb));
    i++;
  }
  const arr = Array.from(set);
  for (let i2 = arr.length - 1; i2 > 0; i2--) {
    const j = Math.floor(Math.random() * (i2 + 1));
    [arr[i2], arr[j]] = [arr[j], arr[i2]];
  }
  return arr;
}

function nonZero(min, max) {
  let v = randInt(min, max);
  if (v === 0) v = randInt(1, max);
  return v;
}

function genAlgEcuaciones(level) {
  let x, question;
  if (level === "facil") {
    const t = randInt(0, 2);
    x = randInt(1, 10);
    if (t === 0) {
      const a = randInt(1, 15);
      question = `x + ${a} = ${x + a}`;
    } else if (t === 1) {
      const a = randInt(2, 9);
      question = `${a}x = ${a * x}`;
    } else {
      x = randInt(2, 12);
      const a = randInt(1, x - 1);
      question = `x − ${a} = ${x - a}`;
    }
  } else if (level === "medio") {
    x = randInt(2, 12);
    const a = randInt(2, 9);
    const b = randInt(1, 20);
    if (Math.random() < 0.5) {
      question = `${a}x + ${b} = ${a * x + b}`;
    } else {
      const result = a * x - b;
      if (result < 0) {
        question = `${a}x + ${b} = ${a * x + b}`;
      } else {
        question = `${a}x − ${b} = ${result}`;
      }
    }
  } else {
    // ax + b = cx + d  with a > c so x positive integer
    x = randInt(2, 10);
    const a = randInt(4, 9);
    const c = randInt(1, a - 1);
    const b = randInt(1, 15);
    const d = a * x + b - c * x;
    if (d < 0) {
      // fallback: simpler
      const b2 = randInt(1, 15);
      const d2 = a * x + b2 - c * x;
      question = `${a}x + ${b2} = ${c}x + ${d2}`;
    } else {
      question = `${a}x + ${b} = ${c}x + ${d}`;
    }
  }
  return {
    question,
    answer: String(x),
    choices: makeIntegerChoices(x, 4),
  };
}

function genAlgPolySumaResta(level, isResta) {
  let p1, p2;
  if (level === "facil") {
    p1 = { 0: nonZero(1, 9), 1: nonZero(1, 9) };
    p2 = { 0: nonZero(1, 9), 1: nonZero(1, 9) };
  } else if (level === "medio") {
    p1 = { 0: nonZero(-9, 9), 1: nonZero(-9, 9) };
    p2 = { 0: nonZero(-9, 9), 1: nonZero(-9, 9) };
  } else {
    p1 = { 0: nonZero(-9, 9), 1: nonZero(-9, 9), 2: nonZero(1, 5) };
    p2 = { 0: nonZero(-9, 9), 1: nonZero(-9, 9), 2: nonZero(1, 5) };
  }
  const result = { 0: 0, 1: 0, 2: 0 };
  for (const e of [0, 1, 2]) {
    result[e] = (p1[e] || 0) + (isResta ? -(p2[e] || 0) : (p2[e] || 0));
  }
  // Ensure non-trivial answer (not 0 everywhere)
  if (result[0] === 0 && result[1] === 0 && result[2] === 0) {
    // perturb p1 slightly and recompute
    p1[0] = (p1[0] || 0) + 1;
    result[0] = (p1[0] || 0) + (isResta ? -(p2[0] || 0) : (p2[0] || 0));
  }
  const op = isResta ? " − " : " + ";
  const q = `(${fmtSinglePoly(p1)})${op}(${fmtSinglePoly(p2)})`;
  return {
    question: q,
    answer: fmtPoly(result),
    choices: makePolyChoices(result),
  };
}

function genAlgPolySuma(level) { return genAlgPolySumaResta(level, false); }
function genAlgPolyResta(level) { return genAlgPolySumaResta(level, true); }

function genAlgMonomiosMulti(level) {
  let a, b, ea, eb;
  if (level === "facil") {
    // (ax)(bx) = abx²
    a = randInt(2, 9); b = randInt(2, 9);
    ea = 1; eb = 1;
  } else if (level === "medio") {
    // (ax^m)(bx^n) with mixed exponents
    a = randInt(2, 9); b = randInt(2, 9);
    ea = randInt(1, 3); eb = randInt(1, 3);
  } else {
    // include negatives
    a = nonZero(-9, 9); b = nonZero(-9, 9);
    ea = randInt(1, 4); eb = randInt(1, 4);
  }
  const productCoef = a * b;
  const productExp = ea + eb;
  const result = { [productExp]: productCoef };
  const q = `(${fmtSinglePoly({ [ea]: a })}) × (${fmtSinglePoly({ [eb]: b })})`;
  return {
    question: q,
    answer: fmtPoly(result),
    choices: makePolyChoices(result),
  };
}

const ALG_SUB_GENERATORS = {
  ecuaciones: genAlgEcuaciones,
  polinomios_suma: genAlgPolySuma,
  polinomios_resta: genAlgPolyResta,
  monomios_multi: genAlgMonomiosMulti,
};

function genAlgebra(level, subOp = "ecuaciones") {
  const fn = ALG_SUB_GENERATORS[subOp] || genAlgEcuaciones;
  return fn(level);
}

// ===================== GEOMETRÍA =====================

const PI = Math.PI;

function fmtGeom(n, decimals) {
  // Avoid trailing .00 when not needed; otherwise fix to N decimals
  if (Number.isInteger(n) && decimals === 0) return String(n);
  return Number(n).toFixed(decimals);
}

function makeGeomChoices(correctStr, correctNum, decimals, spread = 5) {
  const set = new Set([correctStr]);
  let attempts = 0;
  while (set.size < 4 && attempts < 60) {
    attempts++;
    const errKind = Math.floor(Math.random() * 3);
    let cand;
    if (errKind === 0) {
      const delta = randInt(-spread, spread);
      if (delta === 0) continue;
      cand = correctNum + delta;
    } else if (errKind === 1) {
      // Off-by factor (common mistake: multiply by 2 or divide by 2)
      const m = [0.5, 2, 4][randInt(0, 2)];
      cand = correctNum * m;
    } else {
      // Off by 10%
      const sign = Math.random() < 0.5 ? -1 : 1;
      cand = correctNum * (1 + sign * 0.1 * randInt(1, 4));
    }
    if (cand < 0) cand = Math.abs(cand);
    if (Math.abs(cand - correctNum) < Math.pow(10, -decimals) / 2) continue;
    const candStr = fmtGeom(cand, decimals);
    if (candStr !== correctStr) set.add(candStr);
  }
  let i = 1;
  while (set.size < 4) {
    set.add(fmtGeom(correctNum + i * 7, decimals));
    i++;
  }
  return shuffle(Array.from(set));
}

// ----- Perímetros -----
function genGeomPerimetro(level) {
  const decimals = level === "facil" ? 0 : level === "medio" ? 0 : 2;
  const figures = level === "facil"
    ? ["cuadrado", "rectangulo", "triangulo"]
    : ["cuadrado", "rectangulo", "triangulo", "circulo"];
  const fig = figures[randInt(0, figures.length - 1)];

  let question, answer, visual;
  if (fig === "cuadrado") {
    const lado = level === "facil" ? randInt(2, 12) : level === "medio" ? randInt(5, 30) : randInt(8, 50);
    const a = lado * 4;
    question = `Perímetro de un cuadrado de lado ${lado} cm`;
    answer = a;
    visual = { type: "cuadrado", lado };
  } else if (fig === "rectangulo") {
    const base = level === "facil" ? randInt(3, 12) : level === "medio" ? randInt(5, 25) : randInt(10, 40);
    let altura = level === "facil" ? randInt(2, 8) : level === "medio" ? randInt(4, 18) : randInt(6, 30);
    if (altura === base) altura += 1;
    const a = 2 * (base + altura);
    question = `Perímetro de un rectángulo de ${base} cm × ${altura} cm`;
    answer = a;
    visual = { type: "rectangulo", base, altura };
  } else if (fig === "triangulo") {
    // Equilátero o escaleno simple
    if (level === "facil" || Math.random() < 0.4) {
      const lado = level === "facil" ? randInt(2, 10) : level === "medio" ? randInt(4, 20) : randInt(6, 30);
      const a = lado * 3;
      question = `Perímetro de un triángulo equilátero de lado ${lado} cm`;
      answer = a;
      visual = { type: "triangulo", a: lado, b: lado, c: lado };
    } else {
      const a1 = level === "medio" ? randInt(4, 12) : randInt(6, 20);
      const b1 = level === "medio" ? randInt(4, 12) : randInt(6, 20);
      const c1 = level === "medio" ? randInt(4, 12) : randInt(6, 20);
      const a = a1 + b1 + c1;
      question = `Perímetro de un triángulo de lados ${a1}, ${b1} y ${c1} cm`;
      answer = a;
      visual = { type: "triangulo", a: a1, b: b1, c: c1 };
    }
  } else {
    // Círculo (sólo medio/difícil)
    const r = level === "medio" ? randInt(2, 10) : randInt(3, 20);
    const a = 2 * PI * r;
    question = `Perímetro (circunferencia) de un círculo de radio ${r} cm (π ≈ 3.14)`;
    answer = a;
    visual = { type: "circulo", r };
  }

  const ansStr = fmtGeom(answer, decimals);
  return {
    question,
    answer: ansStr,
    choices: makeGeomChoices(ansStr, answer, decimals, level === "facil" ? 4 : 8),
    geomVisual: visual,
    unit: "cm",
  };
}

// ----- Áreas -----
function genGeomArea(level) {
  const decimals = level === "facil" ? 0 : level === "medio" ? 0 : 2;
  const figures = level === "facil"
    ? ["cuadrado", "rectangulo", "triangulo"]
    : ["cuadrado", "rectangulo", "triangulo", "circulo"];
  const fig = figures[randInt(0, figures.length - 1)];

  let question, answer, visual, unit = "cm²";
  if (fig === "cuadrado") {
    const lado = level === "facil" ? randInt(2, 10) : level === "medio" ? randInt(4, 20) : randInt(6, 30);
    answer = lado * lado;
    question = `Área de un cuadrado de lado ${lado} cm`;
    visual = { type: "cuadrado", lado };
  } else if (fig === "rectangulo") {
    const base = level === "facil" ? randInt(3, 10) : level === "medio" ? randInt(5, 20) : randInt(8, 30);
    let altura = level === "facil" ? randInt(2, 8) : level === "medio" ? randInt(3, 15) : randInt(5, 25);
    if (altura === base) altura += 1;
    answer = base * altura;
    question = `Área de un rectángulo de ${base} cm × ${altura} cm`;
    visual = { type: "rectangulo", base, altura };
  } else if (fig === "triangulo") {
    // base × altura / 2, garantizar entero en fácil/medio
    let base, altura;
    if (level === "facil" || level === "medio") {
      // Aseguramos que base*altura sea par para área entera
      for (let i = 0; i < 20; i++) {
        base = level === "facil" ? randInt(2, 12) : randInt(4, 20);
        altura = level === "facil" ? randInt(2, 10) : randInt(3, 16);
        if ((base * altura) % 2 === 0) break;
      }
    } else {
      base = randInt(5, 25);
      altura = randInt(4, 20);
    }
    answer = (base * altura) / 2;
    question = `Área de un triángulo con base ${base} cm y altura ${altura} cm`;
    visual = { type: "triangulo-area", base, altura };
  } else {
    // Círculo
    const r = level === "medio" ? randInt(2, 10) : randInt(3, 15);
    answer = PI * r * r;
    question = `Área de un círculo de radio ${r} cm (π ≈ 3.14)`;
    visual = { type: "circulo", r };
  }

  const ansStr = fmtGeom(answer, decimals);
  return {
    question,
    answer: ansStr,
    choices: makeGeomChoices(ansStr, answer, decimals, level === "facil" ? 5 : 10),
    geomVisual: visual,
    unit,
  };
}

// ----- Pitágoras -----
// Ternas pitagóricas para garantizar resultados enteros en fácil/medio
const PYTH_TRIPLES = [
  [3, 4, 5], [5, 12, 13], [6, 8, 10], [8, 15, 17],
  [7, 24, 25], [9, 12, 15], [9, 40, 41], [12, 16, 20],
  [10, 24, 26], [20, 21, 29], [15, 20, 25], [12, 35, 37],
];

function genGeomPitagoras(level) {
  let a, b, c, question, answer, decimals, unknown;
  if (level === "facil") {
    // Pide la hipotenusa, con terna pitagórica entera
    [a, b, c] = PYTH_TRIPLES[randInt(0, 5)];
    answer = c;
    unknown = "c";
    decimals = 0;
    question = `En un triángulo rectángulo, los catetos miden ${a} y ${b} cm. ¿Cuánto mide la hipotenusa?`;
  } else if (level === "medio") {
    // Mezcla: pide hipotenusa o un cateto con terna entera
    const triple = PYTH_TRIPLES[randInt(0, PYTH_TRIPLES.length - 1)];
    decimals = 0;
    if (Math.random() < 0.5) {
      [a, b, c] = triple;
      answer = c;
      unknown = "c";
      question = `Los catetos de un triángulo rectángulo miden ${a} y ${b} cm. ¿Cuánto mide la hipotenusa?`;
    } else {
      [a, b, c] = triple;
      answer = b;
      unknown = "b";
      question = `La hipotenusa de un triángulo rectángulo mide ${c} cm y un cateto ${a} cm. ¿Cuánto mide el otro cateto?`;
    }
  } else {
    // Difícil: con decimales
    decimals = 2;
    if (Math.random() < 0.5) {
      a = randInt(3, 15);
      b = randInt(3, 15);
      const hyp = Math.sqrt(a * a + b * b);
      answer = hyp;
      c = Number(hyp.toFixed(2));
      unknown = "c";
      question = `Los catetos de un triángulo rectángulo miden ${a} y ${b} cm. ¿Cuánto mide la hipotenusa?`;
    } else {
      const cat = randInt(4, 12);
      const hyp = cat + randInt(3, 8);
      const otro = Math.sqrt(hyp * hyp - cat * cat);
      a = cat; c = hyp;
      b = Number(otro.toFixed(2));
      answer = otro;
      unknown = "b";
      question = `La hipotenusa mide ${hyp} cm y un cateto ${cat} cm. ¿Cuánto mide el otro cateto?`;
    }
  }

  const ansStr = fmtGeom(answer, decimals);
  return {
    question,
    answer: ansStr,
    choices: makeGeomChoices(ansStr, answer, decimals, level === "dificil" ? 6 : 4),
    geomVisual: { type: "pitagoras", a, b, c, unknown },
    unit: "cm",
  };
}

// ----- Ángulos -----
function genGeomAngulos(level) {
  const decimals = 0;
  // Tipos: complementario, suplementario, ángulo faltante en triángulo, polígono
  const types = level === "facil"
    ? ["complementario", "suplementario"]
    : level === "medio"
    ? ["complementario", "suplementario", "triangulo"]
    : ["complementario", "suplementario", "triangulo", "cuadrilatero"];

  const type = types[randInt(0, types.length - 1)];
  let question, answer;

  if (type === "complementario") {
    // Suma a 90°
    const known = randInt(10, 80);
    answer = 90 - known;
    question = `Dos ángulos son complementarios. Si uno mide ${known}°, ¿cuánto mide el otro?`;
  } else if (type === "suplementario") {
    // Suma a 180°
    const known = randInt(20, 160);
    answer = 180 - known;
    question = `Dos ángulos son suplementarios. Si uno mide ${known}°, ¿cuánto mide el otro?`;
  } else if (type === "triangulo") {
    // Suma 180°
    const a = randInt(20, 100);
    const b = randInt(20, 180 - a - 10);
    answer = 180 - a - b;
    question = `Dos ángulos de un triángulo miden ${a}° y ${b}°. ¿Cuánto mide el tercer ángulo?`;
  } else {
    // Cuadrilátero: suma 360°
    const a = randInt(50, 110);
    const b = randInt(50, 110);
    const c = randInt(50, 110);
    answer = 360 - a - b - c;
    if (answer < 20) {
      // Re-randomize avoiding tiny answers
      return genGeomAngulos(level);
    }
    question = `Tres ángulos de un cuadrilátero miden ${a}°, ${b}° y ${c}°. ¿Cuánto mide el cuarto ángulo?`;
  }

  const ansStr = fmtGeom(answer, decimals);
  return {
    question,
    answer: ansStr,
    choices: makeGeomChoices(ansStr, answer, decimals, 8),
    geomVisual: null,
    unit: "°",
  };
}

const GEOM_SUB_GENERATORS = {
  perimetros: genGeomPerimetro,
  areas: genGeomArea,
  pitagoras: genGeomPitagoras,
  angulos: genGeomAngulos,
};

function genGeometria(level, subOp = "perimetros") {
  const fn = GEOM_SUB_GENERATORS[subOp] || genGeomPerimetro;
  return fn(level);
}

const GENERATORS = {
  suma: genSuma,
  resta: genResta,
  multiplicacion: genMultiplicacion,
  division: genDivision,
  porcentajes: genPorcentajes,
  fracciones: genFracciones,
  decimales: genDecimales,
  algebra: genAlgebra,
  geometria: genGeometria,
};

export function generateProblem(operationId, level, subOp) {
  const fn = GENERATORS[operationId];
  if (!fn) throw new Error(`Unknown operation: ${operationId}`);
  if (
    operationId === "fracciones" ||
    operationId === "decimales" ||
    operationId === "algebra" ||
    operationId === "geometria"
  ) return fn(level, subOp);
  return fn(level);
}

export function generateRound(operationId, level, count, subOp) {
  const out = [];
  const seen = new Set();
  let attempts = 0;
  const maxAttempts = count * 30;
  while (out.length < count && attempts < maxAttempts) {
    const p = generateProblem(operationId, level, subOp);
    if (!seen.has(p.question)) {
      seen.add(p.question);
      out.push(p);
    }
    attempts++;
  }
  // Fallback: if we somehow couldn't reach `count` unique problems, fill the rest.
  while (out.length < count) {
    out.push(generateProblem(operationId, level, subOp));
  }
  return out;
}
